// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import clearSearch from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import updateShipTosSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateShipTosSearchFields";
import updateTemporarySearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateTemporarySearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MyAccountPaginatedShipTosDropdown from "@rc/App/Components/MyAccountPaginatedShipTosDropdown/MyAccountPaginatedShipTosDropdown";
import { xScreen } from "@rc/XDL/Utilities";
import debounce from "lodash/debounce";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showShipTo = "showShipTo",
    showInvoiceNumber = "showInvoiceNumber",
    showOrderNumber = "showOrderNumber",
    showRcProductNumber = "showRcProductNumber",
    showPoNumber = "showPoNumber",
    showOrderTotal = "showOrderTotal",
    showDateRange = "showDateRange",
    showStatus = "showStatus",
    // xng-change
    showFulfillmentNumber = "showFulfillmentNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showShipTo]: boolean;
        [fields.showInvoiceNumber]: boolean;
        [fields.showOrderNumber]: boolean;
        [fields.showRcProductNumber]: boolean;
        [fields.showPoNumber]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showDateRange]: boolean;
        [fields.showStatus]: boolean;
        // xng-change
        [fields.showFulfillmentNumber]: boolean;
    };
}

interface State {
    invoiceNumber: string;
    orderNumber: string;
    poNumber: string;
     selectedShipToId: string;
    shipToSearch: string;
    orderTotal: string;
    orderTotalOperator: string;
    rcProductNumber: string;
    shipTosParameter: GetShipTosApiParameter;
    // xng-change
    fulfillmentNumber: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const getInvoicesParameter = state.pages.invoiceHistory.getInvoicesParameter as GetInvoicesApiParameter & {
        properties: string;
    };
    const { filtersOpen, getShipTosParameter } = state.pages.invoiceHistory;
    return {
        filtersOpen,
        shipTosDataView: getShipTosParameter && getShipTosDataView(state, getShipTosParameter),
        getShipTosParameter,
        getInvoicesParameter,
        language: getSession(state).language,
        invoicesParameterProperties: JSON.parse(getInvoicesParameter.properties || "null"),
        rcShowOpenInvoicesToggle: (getSettingsCollection(state).customSetting as any)?.rcShowOpenInvoicesToggle,
    };
};

const mapDispatchToProps = {
    loadShipTos,
    updateShipTosSearchFields,
    updateTemporarySearchFields,
    updateSearchFields,
    loadInvoices,
    clearSearch,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryFilterStyles {
    container?: GridContainerProps;
    headingItem?: GridItemProps;
    shipToGridItem?: GridItemProps;
    shipToSelect?: SelectProps;
    shipToDropdown?: DynamicDropdownPresentationProps;
    invoiceNumberGridItem?: GridItemProps;
    rcProductNumberGridItem?: GridItemProps;
    invoiceNumberText?: TextFieldProps;
    rcProductNumberText?: TextFieldProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberText?: TextFieldProps;
    poNumberGridItem?: GridItemProps;
    poNumberText?: TextFieldProps;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    onlyOpenGridItem?: GridItemProps;
    onlyOpenCheckboxGridItem?: GridItemProps;
    onlyOpenCheckbox?: CheckboxProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearGridItem?: GridItemProps;
    clearFiltersButton?: ButtonProps;
    // xng-change
    fulfillmentNumberGridItem?: GridItemProps;
    showFulfillmentNumberText?: TextFieldProps;
}

export const filterStyles: InvoiceHistoryFilterStyles = {
    headingItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    shipToGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            align-items: flex-end;
        `,
    },
    invoiceNumberGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                margin-top: auto;
            }
        `,
    },
    rcProductNumberGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                margin-top: auto;
            }
        `,
    },
    orderNumberGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                margin-top: auto;
            }
        `,
    },
    poNumberGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                margin-top: auto;
            }
        `,
    },
    fromGridItem: {
        width: [12, 6, 4, 4, 2],
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    toGridItem: {
        width: [12, 6, 4, 4, 2],
        css: css`
            > div {
                width: 100%;
                padding: 0;
                margin-top: auto;
            }
        `,
    },
    onlyOpenGridItem: {
        width: 12,
        align: "bottom",
        css: css`
            padding-top: 0;
        `,
    },
    onlyOpenCheckboxGridItem: {
        width: [12, 12, 6, 6, 4],
        css: css`
            > div {
                margin-top: auto;
            }
        `,
    },
    clearGridItem: {
        width: [12, 12, 6, 6, 4],
        css: css`
            align-items: flex-end;
        `,
    },
    onlyOpenCheckbox: {
        typographyProps: {
            css: css`
                font-weight: 600;
            `,
        },
    },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            align-items: center;
            gap: 32px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
                gap: 26px;
            }
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin-left: auto;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const styles = filterStyles;
class InvoiceHistoryFilter extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            invoiceNumber: this.getValue(props, "invoiceNumber"),
            orderNumber: this.getValue(props, "orderNumber"),
            poNumber: this.getValue(props, "poNumber"),
            selectedShipToId: "",
            shipToSearch: "",
            orderTotal: this.props.invoicesParameterProperties?.orderTotal || "",
            orderTotalOperator: this.props.invoicesParameterProperties?.orderTotalOperator || "",
            rcProductNumber: "",
            shipTosParameter: {
                page: 1,
                pageSize: 15,
                billToId: API_URL_CURRENT_FRAGMENT,
                expand: ["validation"] as (
                    | "validation"
                    | "approvals"
                    | "assignedOnly"
                    | "excludeShowAll"
                    | "excludeOneTime"
                    | "excludeCreateNew"
                )[], // because we include validation, we have to specifically exclude createNew
                exclude: ["createNew"] as ("showAll" | "createNew" | "billTo" | "oneTime")[],
            },
            fulfillmentNumber: "",
        };
    }

    updateTimeoutId: number | undefined;

    getValue = (props: Props, key: "orderNumber" | "invoiceNumber" | "poNumber"): string => {
        let value = props.getInvoicesParameter[key] as string;
        if (!value) {
            value = "";
        }
        return value;
    };

    componentDidUpdate(prevProps: Props) {
        const previousOrderNumber = this.getValue(prevProps, "orderNumber");
        const currentOrderNumber = this.getValue(this.props, "orderNumber");
        const previousInvoiceNumber = this.getValue(prevProps, "invoiceNumber");
        const currentInvoiceNumber = this.getValue(this.props, "invoiceNumber");
        const previousPoNumber = this.getValue(prevProps, "poNumber");
        const currentPoNumber = this.getValue(this.props, "poNumber");

        if (
            (previousOrderNumber !== currentOrderNumber && currentOrderNumber !== this.state.orderNumber) ||
            (previousPoNumber !== currentPoNumber && currentPoNumber !== this.state.poNumber) ||
            (previousInvoiceNumber !== currentInvoiceNumber && currentInvoiceNumber !== this.state.invoiceNumber) ||
            this.props.invoicesParameterProperties?.orderTotalOperator !== this.state.orderTotalOperator
        ) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                invoiceNumber: currentInvoiceNumber,
                orderNumber: currentOrderNumber,
                poNumber: currentOrderNumber,
                orderTotalOperator: this.props.invoicesParameterProperties?.orderTotalOperator,
            });
        }

        if (prevProps.getInvoicesParameter !== this.props.getInvoicesParameter) {
            this.setState({ shipToSearch: "" });
            this.debouncedUpdateSearchFields("");
            this.selectShipTo();
        }

        if (prevProps.getShipTosParameter !== this.props.getShipTosParameter) {
            this.props.loadShipTos(this.props.getShipTosParameter);
        }

        if (prevProps.shipTosDataView !== this.props.shipTosDataView) {
            this.props.shipTosDataView?.value && this.selectShipTo();
        }
    }

    updateParameterAfterTimeout = () => {
        if (typeof this.updateTimeoutId === "number") {
            clearTimeout(this.updateTimeoutId);
        }

        this.updateTimeoutId = setTimeout(() => {
            this.props.updateSearchFields({
                invoiceNumber: this.state.invoiceNumber,
                orderNumber: this.state.orderNumber,
                poNumber: this.state.poNumber,
            });
        }, 250);
    };

    shipToChangeHandler = (customerSequence: string) => {
        this.props.updateSearchFields({ customerSequence: customerSequence || "" });
    };

    selectShipTo = () => {
        const id = this.props.shipTosDataView?.value?.find(
            o => o.customerSequence === (this.props.getInvoicesParameter.customerSequence || ""),
        )?.id;
        id && this.setState({ selectedShipToId: id });
    };

    selectShipToHandler = (id?: string) => {
        id && this.setState({ selectedShipToId: id });
        const customerSequence = this.props.shipTosDataView?.value?.find(o => o.id === id)?.customerSequence;
        this.props.updateSearchFields({ customerSequence });
    };

    changeShipTosSearchTextHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        this.setState({ shipToSearch: value });
        this.debouncedUpdateSearchFields(value);
    };

    debouncedUpdateSearchFields = debounce((value: string) => {
        this.props.updateShipTosSearchFields({ ...this.props.getShipTosParameter, filter: value || undefined });
    }, 200);

    invoiceNumberChangeHandler = (invoiceNumber: string) => {
        this.setState({
            invoiceNumber,
        });

        this.updateParameterAfterTimeout();
    };

    rcProductNumberChangeHandler = (rcProductNumber: string) => {
        this.setState({
            rcProductNumber,
        });

        this.debounceProductNumberChange(rcProductNumber);
    };

    debounceProductNumberChange = debounce(rcProductNumber => {
        this.props.updateSearchFields({
            properties: JSON.stringify({
                productNumber: rcProductNumber,
            }),
        } as any);
    }, 300);

    orderNumberChangeHandler = (orderNumber: string) => {
        this.setState({
            orderNumber,
        });

        this.updateParameterAfterTimeout();
    };

    orderTotalChangeHandler = (event: React.FormEvent<HTMLSelectElement>): void => {
        this.setState({
            orderTotalOperator: event.currentTarget.value,
        });
        this.props.updateSearchFields({
            properties: JSON.stringify({
                orderTotal: this.state.orderTotal,
                orderTotalOperator: event.currentTarget.value,
            }),
        } as any);
    };

    debounceAmountChange = debounce((orderTotal, orderTotalOperator) => {
        this.props.updateSearchFields({
            properties: JSON.stringify({
                orderTotal,
                orderTotalOperator,
            }),
        } as any);
    }, 300);

    handleAmountChange = (event: React.FormEvent<HTMLInputElement>): void => {
        const possibleNumber = Number(event.currentTarget.value);
        if (!Number.isNaN(possibleNumber)) {
            this.setState({
                orderTotal: event.currentTarget.value,
            });
            this.debounceAmountChange(event.currentTarget.value, this.state.orderTotalOperator);
        }
    };

    poNumberChangeHandler = (poNumber: string) => {
        this.setState({
            poNumber,
        });

        this.updateParameterAfterTimeout();
    };

    fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        this.props.updateSearchFields({
            fromDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        this.props.updateSearchFields({
            toDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    showOpenOnlyChangeHandler = (showOpenOnly: boolean) => {
        this.props.updateSearchFields({ showOpenOnly: showOpenOnly || false });
    };

    clearFiltersClickHandler = () => {
        this.props.clearSearch();
        this.setState({
            rcProductNumber: "",
            orderTotalOperator: "",
            orderTotal: "",
            // xng-change
            fulfillmentNumber: "",
        });
    };

    setShipTosParameter = (shipTosParameter: GetShipTosApiParameter) => {
        this.setState({
            ...this.state,
            shipTosParameter,
        });
    };

    // <xng-change>
    fulfillmentNumberChangeHandler = (fulfillmentNumber: string) => {
        this.setState({
            fulfillmentNumber,
        });

        this.debounceFulfillmentNumberChange(fulfillmentNumber);
    };

    debounceFulfillmentNumberChange = debounce(fulfillmentNumber => {
        this.props.updateSearchFields({
            properties: JSON.stringify({
                fulfillmentNumber,
            }),
        } as any);
    }, 300);
    // </xng-change>

    render() {
        const { shipTosDataView } = this.props;
        const { selectedShipToId, shipToSearch } = this.state;
        if (!this.props.filtersOpen) {
            return (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    {this.props.getInvoicesParameter.customerSequence &&
                        this.props.getInvoicesParameter.customerSequence !== "-1" && (
                            <Tag
                                {...styles.appliedFilterTag}
                                onDelete={() => {
                                    this.shipToChangeHandler("-1");
                                }}
                            >
                                {`${translate("Ship To")}: ${this.props.getInvoicesParameter.customerSequence}`}
                            </Tag>
                        )}
                    {this.props.getInvoicesParameter.invoiceNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.invoiceNumberChangeHandler("");
                            }}
                        >
                            {`${translate("Invoice #")}: ${this.props.getInvoicesParameter.invoiceNumber}`}
                        </Tag>
                    )}
                    {this.props.getInvoicesParameter.orderNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.orderNumberChangeHandler("");
                            }}
                        >
                            {`${translate("Order #")}: ${this.props.getInvoicesParameter.orderNumber}`}
                        </Tag>
                    )}
                    {this.props.getInvoicesParameter.poNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.poNumberChangeHandler("");
                            }}
                        >
                            {`${translate("PO #")}: ${this.props.getInvoicesParameter.poNumber}`}
                        </Tag>
                    )}
                    {this.props.getInvoicesParameter.fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.fromDateChangeHandler({});
                            }}
                            data-test-selector="invoiceHistory_fromDateButton"
                        >
                            {`${translate("From")}: ${getLocalizedDateTime({
                                dateTime: convertDateOnlyStringToDate(this.props.getInvoicesParameter.fromDate)!,
                                language: this.props.language,
                            })}`}
                        </Tag>
                    )}
                    {this.props.getInvoicesParameter.toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.toDateChangeHandler({});
                            }}
                        >
                            {`${translate("To")}: ${getLocalizedDateTime({
                                dateTime: convertDateOnlyStringToDate(this.props.getInvoicesParameter.toDate)!,
                                language: this.props.language,
                            })}`}
                        </Tag>
                    )}
                    {this.props.getInvoicesParameter.showOpenOnly && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                this.showOpenOnlyChangeHandler(false);
                            }}
                        >
                            {translate("Only open")}
                        </Tag>
                    )}
                </StyledWrapper>
            );
        }

        const fromDate = this.props.getInvoicesParameter.fromDate
            ? new Date(this.props.getInvoicesParameter.fromDate.replace(/-/g, "/"))
            : undefined;
        const toDate = this.props.getInvoicesParameter.toDate
            ? new Date(this.props.getInvoicesParameter.toDate.replace(/-/g, "/"))
            : undefined;

        return (
            <GridContainer {...styles.container}>
                {this.props.fields.showShipTo && (
                    <GridItem {...styles.shipToGridItem}>
                        <MyAccountPaginatedShipTosDropdown
                            shipTosParameter={this.state.shipTosParameter}
                            setShipTosParameter={this.setShipTosParameter}
                            onChange={this.shipToChangeHandler}
                            shipTo={this.props.getInvoicesParameter.customerSequence}
                            label={translate("Ship-To Address")}
                        />
                    </GridItem>
                )}
                {this.props.fields.showPoNumber && (
                    <GridItem {...styles.poNumberGridItem}>
                        <TextField
                            {...styles.poNumberText}
                            value={this.state.poNumber}
                            label={translate("Customer PO #")}
                            type="poNumber"
                            onChange={event => this.poNumberChangeHandler(event.target.value)}
                            data-test-selector="invoiceHistory_poNumberFilter"
                        />
                    </GridItem>
                )}
                {this.props.fields.showOrderTotal && (
                    <>
                        <GridItem {...styles.fromGridItem} data-test-selector="invoiceHistory_fromDateFilter">
                            <Select
                                data-test-selector="invoiceHistory_filterOrderTotalOperator"
                                label={translate("Order Total")}
                                onChange={this.orderTotalChangeHandler}
                                value={this.state.orderTotalOperator || ""}
                            >
                                <option value="">{translate("Select Option")}</option>
                                {["Greater Than", "Less Than", "Equal To"].map(operator => (
                                    <option key={operator} value={operator}>
                                        {/* this is valid, our rule isn't smart enough to handle this case */}
                                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                        {translate(operator)}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.toGridItem} data-test-selector="invoiceHistory_toDateFilter">
                            <TextField
                                data-test-selector="invoiceHistory_filterOrderTotal"
                                value={this.state.orderTotal}
                                label="Value"
                                type="number"
                                placeholder=""
                                maxLength={6}
                                onChange={this.handleAmountChange}
                            />
                        </GridItem>
                    </>
                )}
                {this.props.fields.showInvoiceNumber && (
                    <GridItem {...styles.invoiceNumberGridItem}>
                        <TextField
                            {...styles.invoiceNumberText}
                            value={this.state.invoiceNumber}
                            label={translate("Invoice #")}
                            onChange={event => this.invoiceNumberChangeHandler(event.target.value)}
                            data-test-selector="invoiceHistory_invoiceNumberFilter"
                        />
                    </GridItem>
                )}
                {this.props.fields.showOrderNumber && (
                    <GridItem {...styles.orderNumberGridItem}>
                        <TextField
                            {...styles.orderNumberText}
                            value={this.state.orderNumber}
                            label={translate("RC PO #")}
                            type="poNumber"
                            onChange={event => this.orderNumberChangeHandler(event.target.value)}
                            data-test-selector="invoiceHistory_erpOrderNumberFilter"
                        />
                    </GridItem>
                )}
                {this.props.fields.showDateRange && (
                    <>
                        <GridItem {...styles.fromGridItem} data-test-selector="invoiceHistory_fromDateFilter">
                            <DatePicker
                                {...styles.fromDate}
                                label={translate("From")}
                                selectedDay={fromDate}
                                onDayChange={this.fromDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    maxDate: toDate,
                                    ...styles.fromDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.toGridItem} data-test-selector="invoiceHistory_toDateFilter">
                            <DatePicker
                                {...styles.toDate}
                                label={translate("To")}
                                selectedDay={toDate}
                                onDayChange={this.toDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    minDate: fromDate,
                                    ...styles.toDate?.dateTimePickerProps,
                                }}
                            />
                        </GridItem>
                    </>
                )}
                {this.props.fields.showRcProductNumber && (
                    <GridItem {...styles.rcProductNumberGridItem}>
                        <TextField
                            {...styles.rcProductNumberText}
                            value={this.state.rcProductNumber}
                            label={translate("Product #")}
                            onChange={event => this.rcProductNumberChangeHandler(event.target.value)}
                            data-test-selector="invoiceHistory_rcProductNumberFilter"
                        />
                    </GridItem>
                )}
                {this.props.fields.showFulfillmentNumber && (
                    <GridItem {...styles.fulfillmentNumberGridItem}>
                        <TextField
                            {...styles.showFulfillmentNumberText}
                            value={this.state.fulfillmentNumber}
                            label={translate("Fulfillment #")}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                this.fulfillmentNumberChangeHandler(event.target.value)
                            }
                            data-test-selector="invoiceHistory_FulfillmentNumberFilter"
                        />
                    </GridItem>
                )}
                {this.props.fields.showStatus && this.props.rcShowOpenInvoicesToggle && (
                    <GridItem {...styles.onlyOpenCheckboxGridItem}>
                        <Checkbox
                            {...styles.onlyOpenCheckbox}
                            checked={this.props.getInvoicesParameter.showOpenOnly}
                            onChange={(_, value) => {
                                this.showOpenOnlyChangeHandler(value);
                            }}
                            data-test-selector="invoiceHistory_showOpenInvoicesFilter"
                        >
                            {translate("Open Invoices only ")}
                        </Checkbox>
                    </GridItem>
                )}
                <GridItem {...styles.clearGridItem}>
                    <Button
                        {...styles.clearFiltersButton}
                        onClick={this.clearFiltersClickHandler}
                        data-test-selector="invoiceHistory_clearFiltersButton"
                    >
                        {translate("Clear")}
                    </Button>
                </GridItem>
            </GridContainer>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryFilter),
    definition: {
        group: "Invoice History",
        displayName: "Search Results Filter",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showShipTo,
                displayName: "Ship to Address",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showOrderNumber,
                displayName: "RC PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showRcProductNumber,
                displayName: "Product #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPoNumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showDateRange,
                displayName: "Date Range",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            // xng-change
            {
                name: fields.showFulfillmentNumber,
                displayName: "Fulfillment Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
